import { createSelector } from 'reselect';
import _compact from 'lodash/compact';
import _flatten from 'lodash/flatten';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _some from 'lodash/some';
import _sortBy from 'lodash/sortBy';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import { templateTypes } from 'yoda-site-components/lib/selectors/productDetails/selectProductType.config';
import { initProductOptions } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import { getProductDetails } from './BundleCommonSelector';
import shippingTagsConfig, {
    SHIPPING_TAGS_SORT_KEY,
} from '../../components/ShippingAndReturns/ShippingTagsConfig';

/**
 * @property {String} CustomBlindsReturns
 * @property {String} deluxed
 * @property {String} EasyReturns
 * @property {String} SameDayPickUp
 * @property {String} ShipToHome
 * @property {String} threshold
 * @property {String} truck
 * @property {String} whiteGlove
 */

const shipToHomeRestrictedSubdivision = ['734', '739'];

const DELUXED = 'DELUXED';
const ROOM_OF_CHOICE = 'ROOM_OF_CHOICE';
const SHIPPING_TAGS_TOKEN = _mapValues(shippingTagsConfig, (_, key) => key);
const THRESHOLD = 'THRESHOLD';
const WHITE_GLOVE = 'WHITE_GLOVE';

const customBlindsReturns = (lots) => {
    const customBlindsSubdivisions = ['733', '734', '739'];
    return customBlindsSubdivisions.includes(_get(lots, '[0].organization.subdivision'));
};

const SHIPPING_TAGS_RULES = {
    [SHIPPING_TAGS_TOKEN.CustomBlindsReturns]: (lots) => customBlindsReturns(lots),
    [SHIPPING_TAGS_TOKEN.EasyReturns]: (lots) => !customBlindsReturns(lots),
    [SHIPPING_TAGS_TOKEN.SameDayPickUp]: (lots) =>
        _some(lots, ({ channelOffering = '' }) => channelOffering.toLowerCase() === 'omnichannel'),
    [SHIPPING_TAGS_TOKEN.ShipToHome]: (lots) =>
        !_some(lots, ({ organization }) =>
            shipToHomeRestrictedSubdivision.includes(_get(organization, 'subdivision', {}))
        ),
    [SHIPPING_TAGS_TOKEN.truck]: (lots) =>
        _some(lots, ({ shipping: { truckable } = '' }) => truckable === !!true),
    [SHIPPING_TAGS_TOKEN.deluxed]: (lots) =>
        _some(lots, ({ deliveryType = '' }) => deliveryType === DELUXED),
    [SHIPPING_TAGS_TOKEN.threshold]: (lots) =>
        _some(lots, ({ deliveryType = '' }) => deliveryType === THRESHOLD),
    [SHIPPING_TAGS_TOKEN.roomOfChoice]: (lots) =>
        _some(lots, ({ deliveryType = '' }) => deliveryType === ROOM_OF_CHOICE),
    [SHIPPING_TAGS_TOKEN.whiteGlove]: (lots) =>
        _some(lots, ({ deliveryType = '' }) => deliveryType === WHITE_GLOVE),
};

const getProductTags = (lots = []) => {
    const productTags = Object.keys(SHIPPING_TAGS_TOKEN).map((tag) => {
        const isVisible = SHIPPING_TAGS_RULES[tag](lots, tag);
        return isVisible ? shippingTagsConfig[tag] : null;
    });

    return _sortBy(_compact(productTags), SHIPPING_TAGS_SORT_KEY);
};

const selectShippingTags = createSelector(
    [getProductDetails, selectProductType(false), initProductOptions(false)],
    (productDetails, { templateType }, { selectedLotDetails }) => {
        let productTags = [];
        if (templateType === templateTypes.BUNDLE) {
            productTags = getProductTags(_flatten(_map(productDetails.products, 'product.lots')));
        } else {
            productTags = !isEmpty(selectedLotDetails) ? getProductTags([selectedLotDetails]) : [];
        }
        return productTags;
    }
);

export default selectShippingTags;
